import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { APIProvider } from "@vis.gl/react-google-maps"
import { useDrag, useDrop } from "react-dnd"
import { useTranslation } from "react-i18next"
import { GripVertical, MoreVertical, Plus } from "lucide-react"
import { AutocompleteGooglePlaces } from "@/components/maps/AutocompleteGooglePlaces"
import TollsInformation from "@/components/maps/TollsInformation"
import type { Toll } from "@/components/maps/RouteView"
import { RouteOptionPickers } from "@/components/chat/RouteOptionPickers"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { moveStop } from "@/lib/routeStopEdits"
import { cn } from "@/lib/utils"
import type { OverviewStop } from "@/lib/driverOverview"
import type {
  PendingRowEdit,
  PendingStopAdd,
} from "@/components/chat/useDriverOverviewRoute"
import {
  PendingStop,
  STOP_ROW,
  StopLabel,
  StopRowShell,
  StopStatus,
} from "@/components/chat/OverviewStopRow"
import type { Location } from "@/reducers/locationsSlice"
import { ACTIONS, stopActionLabel } from "@/reducers/routes2gSlice"

const DRAG_TYPE = "OVERVIEW_ROUTE_STOP"

export type RouteEditing = {
  driverJid: string
  updating: boolean
  pendingAdds: PendingStopAdd[]
  /** The edit each stop is waiting on, by stop key. */
  pendingStops: Record<string, PendingRowEdit>
  onReorder: (keys: string[]) => Promise<void>
  onAdd: (location: Location) => Promise<void>
  onChangeAction: (key: string, action: string) => Promise<void>
  onChangeLocation: (key: string, location: Location) => Promise<void>
  onChangeAvoidTolls: (key: string, avoidTolls: boolean) => Promise<void>
  onRemove: (key: string) => Promise<void>
  tolls: Toll[] | undefined
  tollsVisible: boolean
  tollsLoading: boolean
  tollsLabel: string
  onToggleTolls?: () => void
  routeOptions?: ReactNode
  sendRoute?: { label: string; sending: boolean; onSend: () => void }
}

const AddressSearch = ({
  initialValue,
  onSelect,
  onAbort,
}: {
  initialValue?: string
  onSelect: (location: Location) => void
  onAbort: () => void
}) => {
  const { t } = useTranslation()
  return (
    <APIProvider apiKey={import.meta.env.REACT_APP_GOOGLE_MAP_KEY}>
      <AutocompleteGooglePlaces
        initialValue={initialValue}
        placeholder={t("chat.overviewStops.enterAddress")}
        autoFocus
        onLocationSelect={onSelect}
        onAbort={onAbort}
      />
    </APIProvider>
  )
}

const StopMenu = ({
  stop,
  disabled,
  onChangeAvoidTolls,
  onRemove,
}: {
  stop: OverviewStop
  disabled: boolean
  onChangeAvoidTolls: (avoidTolls: boolean) => void
  onRemove: () => void
}) => {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          aria-label={t("chat.overviewStops.moreFor", { name: stop.name })}
          className="h-7 w-7 shrink-0 text-muted-foreground opacity-0 focus-visible:opacity-100 disabled:opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
        >
          <MoreVertical aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuCheckboxItem
          checked={stop.avoidTolls}
          onCheckedChange={() => onChangeAvoidTolls(!stop.avoidTolls)}
        >
          {t("chat.overviewStops.avoidTolls")}
        </DropdownMenuCheckboxItem>
        <DropdownMenuItem
          inset
          onClick={onRemove}
          className="text-destructive focus:text-destructive"
        >
          {t("chat.overviewStops.removeLocation")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const EditableStop = ({
  stop,
  index,
  editing,
  pending,
  disabled,
  onEdit,
  onEndEdit,
  onMove,
  onDrop,
  onCancel,
  onChangeAction,
  onChangeLocation,
  onChangeAvoidTolls,
  onRemove,
}: {
  stop: OverviewStop
  index: number
  editing: boolean
  pending: PendingRowEdit | undefined
  disabled: boolean
  onEdit: () => void
  onEndEdit: () => void
  onMove: (from: number, to: number) => void
  onDrop: () => void
  onCancel: () => void
  onChangeAction: (action: string) => void
  onChangeLocation: (location: Location) => void
  onChangeAvoidTolls: (avoidTolls: boolean) => void
  onRemove: () => void
}) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLLIElement>(null)
  const canMove = Boolean(stop.movable) && !disabled && !editing
  const action =
    ACTIONS.find((item) => item.value === stop.action?.toLowerCase()) ??
    ACTIONS.find((item) => item.value === "other")!
  const Icon = action.icon
  const [, drop] = useDrop<{ index: number; originalIndex: number }>({
    accept: DRAG_TYPE,
    hover(item, monitor) {
      if (!ref.current || !canMove || item.index === index) return
      const rect = ref.current.getBoundingClientRect()
      const offset = monitor.getClientOffset()
      if (!offset) return
      const middle = (rect.bottom - rect.top) / 2
      const position = offset.y - rect.top
      if (item.index < index && position < middle) return
      if (item.index > index && position > middle) return
      onMove(item.index, index)
      item.index = index
    },
    drop: () => ({}),
  })
  const [{ dragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: { index, originalIndex: index },
    canDrag: () => canMove,
    end(item, monitor) {
      if (item.index === item.originalIndex || !monitor.didDrop()) onCancel()
      else onDrop()
    },
    collect: (monitor) => ({ dragging: monitor.isDragging() }),
  })
  drag(drop(ref))

  if (pending) {
    return (
      <StopRowShell ref={ref} stop={stop}>
        {pending.kind === "remove" ? (
          <PendingStop label={stop.name} verb="Removing" />
        ) : (
          <PendingStop label={pending.address} verb="Saving" />
        )}
      </StopRowShell>
    )
  }

  return (
    <StopRowShell
      ref={ref}
      stop={stop}
      className={cn(
        "group relative",
        canMove && "cursor-grab active:cursor-grabbing",
        dragging && "opacity-40",
      )}
    >
      {canMove && (
        <GripVertical
          className="absolute left-0 h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100"
          aria-hidden
        />
      )}
      {stop.movable && !disabled ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t("chat.overviewStops.stopType", {
                type: stopActionLabel(t, action.value) ?? action.label,
              })}
              className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted"
            >
              <Icon className="h-4 w-4" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[420px]">
            <DropdownMenuRadioGroup
              value={action.value}
              onValueChange={onChangeAction}
            >
              {ACTIONS.map((option) => (
                <DropdownMenuRadioItem key={option.value} value={option.value}>
                  <option.icon className="h-4 w-4" aria-hidden />
                  {stopActionLabel(t, option.value) ?? option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      )}
      {editing ? (
        <div className="min-w-0 flex-1">
          <AddressSearch
            initialValue={stop.address ?? stop.name}
            onSelect={(location) => {
              onEndEdit()
              onChangeLocation(location)
            }}
            onAbort={onEndEdit}
          />
        </div>
      ) : (
        <button
          type="button"
          disabled={!stop.editable || disabled}
          onClick={onEdit}
          aria-label={t("chat.overviewStops.changeAddress", {
            name: stop.name,
          })}
          className="min-w-0 flex-1 truncate rounded-md text-left enabled:hover:bg-muted disabled:cursor-default"
        >
          <StopLabel stop={stop} />
        </button>
      )}
      {!editing && <StopStatus stop={stop} />}
      {stop.movable && !editing && (
        <StopMenu
          stop={stop}
          disabled={disabled}
          onChangeAvoidTolls={onChangeAvoidTolls}
          onRemove={onRemove}
        />
      )}
    </StopRowShell>
  )
}

export const EditableOverviewStops = ({
  stops,
  routeEditing,
}: {
  stops: OverviewStop[]
  routeEditing: RouteEditing
}) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<OverviewStop[] | null>(null)
  const draftRef = useRef<OverviewStop[] | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const reset = useCallback(() => {
    draftRef.current = null
    setDraft(null)
  }, [])
  useEffect(reset, [stops, reset])
  const move = useCallback(
    (from: number, to: number) => {
      const next = moveStop(draftRef.current ?? stops, from, to)
      draftRef.current = next
      setDraft(next)
    },
    [stops],
  )
  const saveOrder = useCallback(async () => {
    if (!draftRef.current) return
    try {
      await routeEditing.onReorder(draftRef.current.map((stop) => stop.id))
    } catch {
      reset()
    }
  }, [reset, routeEditing])
  const shown = draft ?? stops
  return (
    <div
      className="flex min-h-0 flex-1 flex-col px-4 pb-3"
      aria-busy={routeEditing.updating}
    >
      <ol
        data-testid="driver-overview-stops"
        className="max-h-[126px] overflow-y-auto overflow-x-hidden"
      >
        {shown.map((stop, index) => (
          <EditableStop
            key={stop.id}
            stop={stop}
            index={index}
            editing={editing === stop.id}
            pending={routeEditing.pendingStops[stop.id]}
            disabled={routeEditing.updating}
            onEdit={() => setEditing(stop.id)}
            onEndEdit={() => setEditing(null)}
            onMove={move}
            onDrop={saveOrder}
            onCancel={reset}
            onChangeAction={(action) => {
              void routeEditing.onChangeAction(stop.id, action).catch(() => {})
            }}
            onChangeLocation={(location) => {
              void routeEditing
                .onChangeLocation(stop.id, location)
                .catch(() => {})
            }}
            onChangeAvoidTolls={(avoidTolls) => {
              void routeEditing
                .onChangeAvoidTolls(stop.id, avoidTolls)
                .catch(() => {})
            }}
            onRemove={() => {
              void routeEditing.onRemove(stop.id).catch(() => {})
            }}
          />
        ))}
        {routeEditing.pendingAdds.map(({ key, address }) => (
          <li
            key={key}
            data-testid="driver-overview-stop-pending"
            className={STOP_ROW}
          >
            <PendingStop label={address} verb="Saving" />
          </li>
        ))}
      </ol>
      <div className={STOP_ROW}>
        <Plus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        {editing === "__add_stop__" ? (
          <div className="min-w-0 flex-1">
            <AddressSearch
              onSelect={(location) => {
                setEditing(null)
                void routeEditing.onAdd(location).catch(() => {})
              }}
              onAbort={() => setEditing(null)}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing("__add_stop__")}
            disabled={routeEditing.updating}
            className="min-w-0 flex-1 truncate rounded-md text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          >
            {t("chat.overviewStops.addStop")}
          </button>
        )}
      </div>
      {routeEditing.tollsVisible && (
        <TollsInformation tolls={routeEditing.tolls} className="mx-0 mt-2" />
      )}
      <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
        {routeEditing.onToggleTolls && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={routeEditing.onToggleTolls}
            disabled={routeEditing.tollsLoading}
            aria-expanded={routeEditing.tollsVisible}
            className="h-7 px-2.5 text-sm"
          >
            {routeEditing.tollsLabel}
          </Button>
        )}
        {routeEditing.routeOptions ?? (
          <RouteOptionPickers driverJid={routeEditing.driverJid} />
        )}
      </div>
    </div>
  )
}
